import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useInvalidateOnAdvancingActivity } from "advancing/activity/useInvalidateOnAdvancingActivity";
import { AdvancingDeclinedText } from "advancing/components/AdvancingDeclinedText";
import { AdvancingTravelPanel } from "advancing/components/AdvancingTravelPanel";
import { ReadonlyAdvancingValue } from "advancing/components/ReadonlyAdvancingValue";
import { AdvancingChatPanel } from "advancing/messages/AdvancingChatPanel";
import { getAdvancingProvidedValue, getAdvancingRowValue, isEmptyAdvancingValue, toAdvancingInputValue } from "advancing/utils";
import { DeclineFormRowModal } from "advancingapprove/components/DeclineFormRowModal";
import { getApproveSidePanelQuery } from "advancingapprove/utils";
import BrandLogo from "components/BrandLogo";
import AreYouSureModal from "components/modals/AreYouSureModal";
import { AdvancingApproveSectionTemplate } from "components/MainSectionTemplate/AdvancingApproveSectionTemplate";
import TwoColumnLayout from "layout/TwoColumnLayout";
import { getLinkedTravelInfo } from "linkedtravel/components/linkedtravelrow";
import React, { useState } from "react";
import { useLoaderData } from "react-router";
import { useCustomQuery } from "utilities/hookutils";

type RowAction = "approve" | "decline" | "remove";

const rowActionMutationKey = ["AdvancingSession.RowAction"];

interface RowActionInstruction {
  action: RowAction
  advancingFormItemId: number
  declinedReason?: string
}

const renderValue = (row: server.dto.AdvancingFormRow, serverValue: string) => {
  const value = toAdvancingInputValue(row.formElementType, serverValue);
  return isEmptyAdvancingValue(row.formElementType, value)
    ? <span>{RESX.Advancing.NoValue}</span>
    : <ReadonlyAdvancingValue type={row.formElementType} value={value} serverValue={serverValue} dropdownOptions={row.dropdownOptions} />;
};

export const CheckAndApproveTab = () => {
  const viewModel = useLoaderData<server.viewmodel.AdvancingApproveFrontModel>();
  const { advancingSessionId } = viewModel.advancingSession;
  const sidePanelQueryOptions = getApproveSidePanelQuery(advancingSessionId);
  const sidePanelQueryKey = sidePanelQueryOptions.queryKey;
  const queryClient = useQueryClient();
  const [rowActions, setRowActions] = useState<Record<string, RowAction>>({});
  const [declineRow, setDeclineRow] = useState<server.dto.AdvancingFormRow>();
  const [removeRow, setRemoveRow] = useState<server.dto.AdvancingFormRow>();

  const sidePanelQuery = useCustomQuery(sidePanelQueryOptions);

  useInvalidateOnAdvancingActivity(advancingSessionId, sidePanelQueryKey, { skipWhileMutating: rowActionMutationKey });

  const rowActionMutation = useMutation({
    mutationKey: rowActionMutationKey,
    mutationFn: ({ action, advancingFormItemId, declinedReason }: RowActionInstruction) => {
      const instruction = { advancingFormItemId, advancingSessionId };
      const config = { toastOnError: true };

      switch (action) {
        case "approve": return window.CoreApi.AdvancingSession.ApproveFormRow(instruction, config).then(r => r.data);
        case "decline": return window.CoreApi.AdvancingSession.DeclineFormRow({ ...instruction, declinedReason }, config).then(r => r.data);
        case "remove": return window.CoreApi.AdvancingSession.RemoveFormRow(instruction, config).then(r => r.data);
      }
    },
    onMutate: ({ action, advancingFormItemId }) => {
      setRowActions(prev => ({ ...prev, [advancingFormItemId]: action }));
    },
    onSuccess: async (response, { advancingFormItemId }) => {
      if (response.hasErrors) return;
      await queryClient.invalidateQueries({ queryKey: sidePanelQueryKey });
      // Another row's modal may have been opened in the meantime
      setDeclineRow(prev => prev?.advancingFormItemId === advancingFormItemId ? undefined : prev);
    },
    onSettled: (_response, _error, { advancingFormItemId }) => {
      setRowActions(prev => {
        const { [advancingFormItemId]: _, ...rest } = prev;
        return rest;
      });
    }
  });

  const hideTravelMutation = useMutation({
    mutationFn: ({ showId, appointment }: server.dto.ShowToTravelAppointment) => {
      const instruction = { showId, itemId: getLinkedTravelInfo(appointment).id, displayInAdvancing: false };
      const config = { toastOnError: true };

      switch (appointment.type) {
        case "flight": return window.CoreApi.Show.LinkFlight(instruction, config).then(r => r.data);
        case "train": return window.CoreApi.Show.LinkTrain(instruction, config).then(r => r.data);
        case "groundTransport": return window.CoreApi.Show.LinkGroundTransport(instruction, config).then(r => r.data);
        default: throw new Error(`Unexpected travel type: ${appointment.type}`);
      }
    },
    onMutate: async (travelAppointment) => {
      await queryClient.cancelQueries({ queryKey: sidePanelQueryKey });
      queryClient.setQueryData<server.dto.ApiResponseWrapper<server.dto.AdvancingSessionSidePanelData>>(sidePanelQueryKey, response => ({
        ...response,
        data: { ...response.data, travelAppointments: response.data.travelAppointments.filter(t => t !== travelAppointment) }
      }));
    },
    onSettled: (_response, _error, { showId }) => {
      queryClient.invalidateQueries({ queryKey: sidePanelQueryKey });
      queryClient.invalidateQueries({ queryKey: ["linkedTravel", showId] });
    }
  });

  return <TwoColumnLayout>
    <TwoColumnLayout.LeftColumn>
      {sidePanelQuery.data
        ? <AdvancingApproveSectionTemplate
          sections={sidePanelQuery.data.data.formGroups.map((group, idx) => ({
            key: `${idx}_${group.label}`,
            title: group.label,
            fields: group.rows.map(row => {
              const serverValue = getAdvancingRowValue(row);
              const value = toAdvancingInputValue(row.formElementType, serverValue);
              const isPending = row.formItemPhase === "pending";
              const rowAction = rowActions[row.advancingFormItemId];

              return {
                key: row.advancingFormItemId.toString(),
                label: row.label,
                sublabel: row.sublabel,
                phase: row.formItemPhase,
                formElementBehavior: row.formElementBehavior,
                // Pending rows render their own empty values, so the current value always shows
                isEmpty: !isPending && isEmptyAdvancingValue(row.formElementType, value),
                render: () => isPending
                  ? <div>
                    {renderValue(row, getAdvancingProvidedValue(row))}
                    <div className="text-sublabel flex gap-small">
                      <span>{RESX.Advancing.Currently}:</span>
                      <div>{renderValue(row, row.originalValue)}</div>
                    </div>
                  </div>
                  : <ReadonlyAdvancingValue type={row.formElementType} value={value} serverValue={serverValue} dropdownOptions={row.dropdownOptions} />,
                footer: row.formItemPhase === "declined" ? <AdvancingDeclinedText row={row} /> : undefined,
                actorName: row.actorName,
                lastModifiedUtc: row.lastModifiedUtc,
                onRemove: () => {
                  if (rowAction) return;
                  setRemoveRow(row);
                },
                onValueClick: row.formItemPhase === "declined" ? () => {
                  if (rowAction) return;
                  setDeclineRow(row);
                } : undefined,
                approveButtons: {
                  isVisible: isPending,
                  isApproving: rowAction === "approve",
                  isDeclining: rowAction === "decline",
                  onApprove: () => {
                    if (rowAction) return;
                    rowActionMutation.mutate({ action: "approve", advancingFormItemId: row.advancingFormItemId });
                  },
                  onDecline: () => {
                    if (rowAction) return;
                    setDeclineRow(row);
                  },
                },
              };
            })
          }))}
        />
        : <AdvancingApproveSectionTemplate isLoading />}
      {removeRow && <AreYouSureModal
        title={RESX.Advancing.RemoveFromAdvancingSession}
        yesButtonLabel={RESX.Buttons.Remove}
        yesButtonColor="red"
        onYes={() => {
          // Resolves after the refetch, so the modal keeps loading until the row is gone
          return rowActionMutation.mutateAsync({ action: "remove", advancingFormItemId: removeRow.advancingFormItemId });
        }}
        onClose={() => setRemoveRow(undefined)}
      />}
      {declineRow && <DeclineFormRowModal
        title={declineRow.label}
        declinedReason={declineRow.declinedReason}
        isDeclining={rowActions[declineRow.advancingFormItemId] === "decline"}
        onCancel={() => setDeclineRow(undefined)}
        onDecline={declinedReason => {
          if (rowActions[declineRow.advancingFormItemId]) return;
          rowActionMutation.mutate({ action: "decline", advancingFormItemId: declineRow.advancingFormItemId, declinedReason });
        }}
      />}
    </TwoColumnLayout.LeftColumn>
    <TwoColumnLayout.RightColumn size="medium" gap="bigger">
      <BrandLogo brand={viewModel.advancingSession.brand} />
      <AdvancingTravelPanel
        isLoading={sidePanelQuery.isLoading}
        travelAppointments={sidePanelQuery.data?.data.travelAppointments ?? []}
        onHide={sidePanelQuery.data?.data.canLinkUnlinkTravel ? (travelAppointment) => hideTravelMutation.mutate(travelAppointment) : undefined} />
      <AdvancingChatPanel advancingSessionId={advancingSessionId} incomingMessageType="toAgent" />
    </TwoColumnLayout.RightColumn>
  </TwoColumnLayout>;
};
